import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, obj=auth
  const [orgs, setOrgs] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);

  const applyOrgs = useCallback((organizations) => {
    setOrgs(organizations || []);
    const stored = localStorage.getItem("wh_org");
    const found = (organizations || []).find((o) => o.id === stored) || (organizations || [])[0];
    if (found) {
      setCurrentOrg(found);
      localStorage.setItem("wh_org", found.id);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      applyOrgs(data.organizations);
    } catch {
      setUser(false);
    }
  }, [applyOrgs]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("wh_token", data.token);
    await refresh();
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("wh_token", data.token);
    await refresh();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("wh_token");
    localStorage.removeItem("wh_org");
    setUser(false);
    setOrgs([]);
    setCurrentOrg(null);
  };

  const switchOrg = (org) => {
    setCurrentOrg(org);
    localStorage.setItem("wh_org", org.id);
    window.location.reload();
  };

  const can = (perm) => {
    const role = currentOrg?.role;
    const map = {
      owner: ["*"], super_admin: ["*"],
      admin: ["view_org","manage_users","manage_branches","manage_departments","manage_clients","manage_projects","create_tasks","manage_files","use_chat","manage_deals","manage_tickets","view_financials","view_reports","export","manage_settings","manage_workflows"],
      manager: ["view_org","manage_clients","manage_projects","create_tasks","use_chat","manage_deals","manage_tickets","view_reports","export"],
      employee: ["view_org","create_tasks","use_chat"],
      client: ["view_org"], guest: ["view_org"],
    };
    const perms = map[role] || [];
    return perms.includes("*") || perms.includes(perm);
  };

  const canAccess = (moduleKey) => {
    if (moduleKey === "video") return true;
    const mods = currentOrg?.modules;
    if (!mods) return true; // still loading — avoid false redirects
    return mods.includes(moduleKey);
  };

  const canManageAccess = () => can("manage_users") || currentOrg?.role === "manager";

  return (
    <AuthContext.Provider value={{ user, orgs, currentOrg, login, register, logout, switchOrg, refresh, can, canAccess, canManageAccess }}>
      {children}
    </AuthContext.Provider>
  );
}
